import { create } from 'zustand';
import axiosInstance from '../api/api';
import { API_ROUTES } from '../config/api-routes';
import { parseHubRecruiterDisplay } from '../components/MessageHub/messageHubRecruiter';

const HUB_CACHE_TTL_MS = 2 * 60 * 1000;

type FetchOptions = { force?: boolean };

export type HubConversation = any;
export type HubQuestion = {
  id: string;
  recruiter: string;
  company: string;
  section: string;
  question: string;
  questionItems?: Array<{ section?: string; questionText?: string }>;
  status: 'pending' | 'answered' | 'resolved';
  timeIso: string;
  timeDisplay: string;
  reply?: string;
  replyAttachments?: Array<{
    id?: string;
    fileName?: string;
    fileUrl: string;
    type?: string;
    mimeType?: string;
  }>;
  conversationId?: string;
};
export type HubContact = any;
export type HubRequest = any;

interface MessageHubState {
  conversations: HubConversation[];
  questions: HubQuestion[];
  contacts: HubContact[];
  requests: HubRequest[];

  conversationsLoading: boolean;
  questionsLoading: boolean;
  contactsLoading: boolean;
  requestsLoading: boolean;

  conversationsFetchedAt: number | null;
  questionsFetchedAt: number | null;
  contactsFetchedAt: number | null;
  requestsFetchedAt: number | null;

  fetchConversations: (opts?: FetchOptions) => Promise<HubConversation[]>;
  fetchQuestions: (opts?: FetchOptions) => Promise<HubQuestion[]>;
  fetchContacts: (opts?: FetchOptions) => Promise<HubContact[]>;
  fetchRequests: (opts?: FetchOptions) => Promise<HubRequest[]>;

  setQuestions: (questions: HubQuestion[]) => void;
  upsertQuestionFromRealtime: (row: Record<string, unknown>) => void;
  setContacts: (contacts: HubContact[]) => void;
  setRequests: (requests: HubRequest[]) => void;
}

let conversationsInFlight: Promise<HubConversation[]> | null = null;
let questionsInFlight: Promise<HubQuestion[]> | null = null;
let contactsInFlight: Promise<HubContact[]> | null = null;
let requestsInFlight: Promise<HubRequest[]> | null = null;

function formatHubRelativeTime(input: string) {
  try {
    const date = new Date(input);
    const now = Date.now();
    const diffMs = Math.max(0, now - date.getTime());
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
}

function isFresh(ts: number | null) {
  if (!ts) return false;
  return Date.now() - ts < HUB_CACHE_TTL_MS;
}

function mapApiQuestionRow(q: Record<string, unknown>): HubQuestion {
  const timeIso = String(q.updatedAt || q.createdAt || new Date().toISOString());
  const recruiterRaw = (q.recruiter as Record<string, unknown>) || {};
  const { name, company } = parseHubRecruiterDisplay(recruiterRaw);
  const statusRaw = String(q.status || 'pending').toLowerCase();
  const status: HubQuestion['status'] =
    statusRaw === 'answered' ? 'answered' : statusRaw === 'resolved' ? 'resolved' : 'pending';
  return {
    id: String(q.id),
    recruiter: name,
    company,
    question: String(q.questionText || ''),
    questionItems: Array.isArray(q.questionItems)
      ? (q.questionItems as HubQuestion['questionItems'])
      : undefined,
    reply: q.answerText ? String(q.answerText) : '',
    replyAttachments: Array.isArray(q.answerAttachments)
      ? (q.answerAttachments as HubQuestion['replyAttachments'])
      : [],
    section: String(q.section || 'General'),
    status,
    timeIso,
    timeDisplay: formatHubRelativeTime(timeIso),
    conversationId: (() => {
      const conv = q.conversation as Record<string, unknown> | undefined;
      const raw = conv?.id ?? q.conversationId ?? q.conversationUuid;
      return raw ? String(raw) : undefined;
    })(),
  };
}

export const useMessageHubStore = create<MessageHubState>((set, get) => ({
  conversations: [],
  questions: [],
  contacts: [],
  requests: [],

  conversationsLoading: false,
  questionsLoading: false,
  contactsLoading: false,
  requestsLoading: false,

  conversationsFetchedAt: null,
  questionsFetchedAt: null,
  contactsFetchedAt: null,
  requestsFetchedAt: null,

  fetchConversations: async (opts) => {
    const state = get();
    if (!opts?.force && state.conversations.length && isFresh(state.conversationsFetchedAt)) {
      return state.conversations;
    }
    if (!opts?.force && conversationsInFlight) return conversationsInFlight;

    conversationsInFlight = (async () => {
      set({ conversationsLoading: true });
      try {
        const response = await axiosInstance.get(API_ROUTES.MESSAGESHUB.GET_CONVERSATIONS);
        const rows = (response.data?.data || []).map((conversation: any) => {
          const participants = Array.isArray(conversation.participants)
            ? conversation.participants.map((participant: any) => {
                const { name, company } = parseHubRecruiterDisplay({
                  ...participant,
                  email: participant.email,
                  name: participant.name,
                  company: participant.company,
                });
                return {
                  ...participant,
                  name,
                  company: participant.company || company,
                };
              })
            : conversation.participants;
          return { ...conversation, participants };
        });
        set({
          conversations: rows,
          conversationsLoading: false,
          conversationsFetchedAt: Date.now(),
        });
        return rows;
      } finally {
        set({ conversationsLoading: false });
        conversationsInFlight = null;
      }
    })();
    return conversationsInFlight;
  },

  fetchQuestions: async (opts) => {
    const state = get();
    if (!opts?.force && state.questions.length && isFresh(state.questionsFetchedAt)) {
      return state.questions;
    }
    if (!opts?.force && questionsInFlight) return questionsInFlight;

    questionsInFlight = (async () => {
      set({ questionsLoading: true });
      try {
        const response = await axiosInstance.get(API_ROUTES.MESSAGESHUB.GET_QUESTIONS);
        const rows = (response.data?.data || []).map((q: any) => mapApiQuestionRow(q));
        set({
          questions: rows,
          questionsLoading: false,
          questionsFetchedAt: Date.now(),
        });
        return rows;
      } finally {
        set({ questionsLoading: false });
        questionsInFlight = null;
      }
    })();
    return questionsInFlight;
  },

  fetchContacts: async (opts) => {
    const state = get();
    if (!opts?.force && state.contacts.length && isFresh(state.contactsFetchedAt)) {
      return state.contacts;
    }
    if (!opts?.force && contactsInFlight) return contactsInFlight;

    contactsInFlight = (async () => {
      set({ contactsLoading: true });
      try {
        const response = await axiosInstance.get(API_ROUTES.MESSAGESHUB.GET_CONTACTS);
        const rows = (response.data?.data || []).map((contact: any) => {
          const { name, company } = parseHubRecruiterDisplay(contact.recruiter);
          const recruiter = contact.recruiter || {};
          const nameParts = name.split(' ');
          return {
            ...contact,
            recruiter: {
              ...recruiter,
              firstName: recruiter.firstName || nameParts[0] || '',
              lastName: recruiter.lastName || nameParts.slice(1).join(' ') || '',
              name,
              company: recruiter.company || company,
            },
          };
        });
        set({
          contacts: rows,
          contactsLoading: false,
          contactsFetchedAt: Date.now(),
        });
        return rows;
      } finally {
        set({ contactsLoading: false });
        contactsInFlight = null;
      }
    })();
    return contactsInFlight;
  },

  fetchRequests: async (opts) => {
    const state = get();
    if (!opts?.force && state.requests.length && isFresh(state.requestsFetchedAt)) {
      return state.requests;
    }
    if (!opts?.force && requestsInFlight) return requestsInFlight;

    requestsInFlight = (async () => {
      set({ requestsLoading: true });
      try {
        const response = await axiosInstance.get(API_ROUTES.MESSAGESHUB.GET_REQUESTS_FOR_CANDIDATE);
        const rows = (response.data?.data || []).map((req: any) => {
          const { name, company } = parseHubRecruiterDisplay(req.recruiter);
          return {
            id: req.id,
            recruiter: {
              id: req.recruiter?.id || 0,
              fullName: name,
              company,
              profileImg: req.recruiter?.profileImg || req.recruiter?.profile_image || null,
            },
            status: req.status?.toLowerCase() || 'pending',
            createdAt: req.createdAt,
          };
        });
        set({
          requests: rows,
          requestsLoading: false,
          requestsFetchedAt: Date.now(),
        });
        return rows;
      } finally {
        set({ requestsLoading: false });
        requestsInFlight = null;
      }
    })();
    return requestsInFlight;
  },

  setQuestions: (questions) => set({ questions }),
  upsertQuestionFromRealtime: (row) => {
    const mapped = mapApiQuestionRow(row);
    set((state) => {
      const index = state.questions.findIndex((question) => question.id === mapped.id);
      if (index === -1) {
        return { questions: [mapped, ...state.questions] };
      }
      const next = [...state.questions];
      next[index] = { ...next[index], ...mapped };
      return { questions: next };
    });
  },
  setContacts: (contacts) => set({ contacts }),
  setRequests: (requests) => set({ requests }),
}));
